/**
 * Axios auth wiring (imported once for its side effects in index.js).
 *
 * - Request: attach the staff bearer token (if any) to every call.
 * - Response: on 401 for a protected endpoint, drop the token and bounce to
 *   /login. Public guest endpoints (/api/public/*) and the login call itself
 *   are exempt so the guest unlock page never redirects.
 */

import axios from 'axios';

export const TOKEN_KEY = 'gl_token';
export const USER_KEY = 'gl_user';

export function getToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch (_) { return null; }
}

export function setToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch (_) { /* ignore */ }
}

export function getStoredUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) { return null; }
}

export function setStoredUser(user) {
  try {
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_KEY);
  } catch (_) { /* ignore */ }
}

axios.interceptors.request.use((config) => {
  const token = getToken();
  // Never clobber an explicitly set Authorization header (the kiosk page
  // sends its own kiosk-role bearer token per request).
  if (token && !(config.headers && config.headers.Authorization)) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = (error && error.config && error.config.url) || '';
    const status = error && error.response && error.response.status;
    const isPublic = url.includes('/api/public') || url.includes('/api/auth/login');
    if (status === 401 && !isPublic) {
      const path = window.location.pathname;
      // The kiosk page manages its own (separate) token and sign-in screen.
      if (!path.startsWith('/kiosk')) {
        setToken(null);
        setStoredUser(null);
        if (!path.startsWith('/login') && !path.startsWith('/unlock')) {
          window.location.assign('/login');
        }
      }
    }
    return Promise.reject(error);
  }
);
